import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Paperclip, Save, X } from 'react-feather';
import Screen from '../Layouts/Screen/Screen';
import Spinner from '../../global-components/src/components/spinner/spiner';
import { useAuth, useMessage } from '../../global-components/src';
import { errorMessage } from '../../global-components/src/utils/text';
import { api } from '../../services/api';
import './moderator-actions.css';

interface GlobalPinnedPost {
    post_id: number | null;
    message?: string;
}

const GlobalPinnedPost = () => {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const [postId, setPostId] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadPinnedPost = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api(token).get<GlobalPinnedPost>('/pacoca-tools/global-pinned-post');
            setPostId(data.post_id ? String(data.post_id) : '');
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    }, [setMessage, token]);

    useEffect(() => {
        void loadPinnedPost();
    }, [loadPinnedPost]);

    const savePinnedPost = async (event: FormEvent) => {
        event.preventDefault();
        const normalizedPostId = postId.trim();

        if (!normalizedPostId) {
            setMessage('Informe o ID do post que será fixado.', 'error');
            return;
        }

        if (!window.confirm(`Fixar o post #${normalizedPostId} no início do feed de toda a rede?`)) return;

        setSaving(true);
        try {
            const { data } = await api(token).put<GlobalPinnedPost>('/pacoca-tools/global-pinned-post', {
                post_id: normalizedPostId,
            });
            setPostId(data.post_id ? String(data.post_id) : '');
            setMessage(data.message ?? 'Post fixado para toda a rede.', 'success');
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setSaving(false);
        }
    };

    const clearPinnedPost = async () => {
        if (!window.confirm('Remover o post fixado do feed de toda a rede?')) return;

        setSaving(true);
        try {
            const { data } = await api(token).put<GlobalPinnedPost>('/pacoca-tools/global-pinned-post', {
                post_id: null,
            });
            setPostId('');
            setMessage(data.message ?? 'Post fixado global removido.', 'success');
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Screen title="Post fixado na rede" paddingTopTitle>
            <Helmet>
                <title>Paçoca - Post fixado na rede</title>
            </Helmet>

            <div className="moderator-action-page">
                <section className="moderator-action-card">
                    <div className="moderator-action-heading">
                        <div>
                            <h2><Paperclip size={21} /> Fixação global</h2>
                            <p>Informe o ID de um post para exibi-lo no início do feed. A configuração é salva somente no cache.</p>
                        </div>
                    </div>

                    <form className="moderator-global-pin-form" onSubmit={savePinnedPost}>
                        <div className="moderator-action-field">
                            <label htmlFor="global-pinned-post-id">ID do post</label>
                            <input
                                id="global-pinned-post-id"
                                className="form-control"
                                type="number"
                                min="1"
                                step="1"
                                inputMode="numeric"
                                required
                                disabled={loading || saving}
                                value={postId}
                                onChange={event => setPostId(event.target.value)}
                                placeholder="Ex.: 577537"
                            />
                        </div>

                        <div className="moderator-action-buttons">
                            <button className="btn btn-primary" type="submit" disabled={loading || saving}>
                                {saving ? <Spinner /> : <Save size={17} />}
                                Salvar post fixado
                            </button>
                            {postId && (
                                <button className="btn btn-light" type="button" disabled={loading || saving} onClick={() => void clearPinnedPost()}>
                                    <X size={17} /> Remover fixação
                                </button>
                            )}
                        </div>
                    </form>
                </section>
            </div>
        </Screen>
    );
};

export default GlobalPinnedPost;
